import type { SearchRow } from "@/services/api/search";
import { inputMethodManager } from "@/utils/pinyin";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Grid, Input, Spin, Tag } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./SearchPanelCard.less";

interface SearchPanelCardProps {
  value: string;
  onChange: (val: string) => void;
  onSearch?: (kw?: string | SearchRow) => void;
  rows?: SearchRow[];
  showRows?: boolean;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onSelectRow?: (row: SearchRow) => void;
  compact?: boolean; // 新增：紧凑模式（输入内置按钮、窄宽度）
  onCloseRows?: () => void; // 新增：关闭下拉
  onFetchSuggestions?: (kw?: string) => void; // 新增：获取搜索建议（不跳转）
}

// 固定的默认数据（包含完整的品牌和行业信息）
const DEFAULT_ROWS: SearchRow[] = [
  // {
  //   brandId: "87835741-1533-40ea-ae5d-41963720665c",
  //   brand: "小鹏",
  //   industryId: "d9c217b6-1711-4c7f-b9ee-8d2713a718a5",
  //   currentCategoryName: "新能源车",
  //   tags: ["新能源车"],
  // },
  // {
  //   brandId: "019a3e01-0994-780d-959e-54b85416d0f4",
  //   brand: "Nike",
  //   industryId: "019a3e01-0994-7965-aadd-02426092841a",
  //   currentCategoryName: "运动鞋服",
  //   tags: ["运动鞋服"],
  // },
  // {
  //   brandId: "019a39a8-d9a9-7e92-aea0-3b23f0caa7df",
  //   brand: "麦肯锡",
  //   industryId: "019a39a8-d9a9-7ba8-bb46-c9e7c21224f9",
  //   currentCategoryName: "咨询",
  //   tags: ["咨询"],
  // },
  // {
  //   brandId: "019a39a8-d9aa-73fb-8856-0e624a90cd77",
  //   brand: "慕课网",
  //   industryId: "019a39a8-d9a9-7bc3-9065-9baaaabd0f40",
  //   currentCategoryName: "程序员在线培训",
  //   tags: ["程序员在线培训"],
  // },
  // {
  //   brandId: "019a3d77-9584-75df-b582-6857c43ca154",
  //   brand: "杜蕾斯",
  //   industryId: "019a3d77-9584-7645-8350-7f8425ac7581",
  //   currentCategoryName: "避孕套",
  //   tags: ["避孕套"],
  // },
  // {
  //   brandId: "019a3d77-95a0-7288-8a63-768ac3ac21af",
  //   brand: "有赞",
  //   industryId: "019a3d77-95a0-72ce-8178-05a5013ce197",
  //   currentCategoryName: "电商OMS",
  //   tags: ["电商OMS"],
  // },
  // {
  //   brandId: "019a3e00-fdf4-7eca-ae07-70c6234b0d79",
  //   brand: "用友建筑云",
  //   industryId: "019a3e00-fdf5-7072-81d1-98a2bda7cfac",
  //   currentCategoryName: "工程管理系统",
  //   tags: ["工程管理系统"],
  // },
  // {
  //   brandId: "019a4e41-741c-7c60-a906-89ee2f687e94",
  //   brand: "易方达基金",
  //   industryId: "019a4e2e-4959-75a7-9983-12b9a1fe3881",
  //   currentCategoryName: "金融-公募基金",
  //   tags: ["金融-公募基金"],
  // },
];

// 获取默认数据的函数（直接返回固定数据，不再请求API）
const getDefaultRows = (): SearchRow[] => {
  return DEFAULT_ROWS;
};

export const SearchPanelCard: React.FC<SearchPanelCardProps> = ({
  value,
  onChange,
  onSearch,
  rows = [],
  showRows = false,
  isLoading = false,
  error = null,
  onRetry,
  onSelectRow,
  compact = false,
  onCloseRows,
  onFetchSuggestions,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.lg;
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = React.useState(false);

  // 输入完成状态监控
  const [inputState, setInputState] = useState({
    isComposing: false, // 输入法是否正在组合
    isComplete: true, // 输入是否完成
    lastCompleteValue: "", // 最后完成的输入值
  });

  // 获取默认数据（使用固定数据，无需异步请求）
  // 直接初始化 useState 时设置默认数据，无需 useEffect
  const [defaultRows, setDefaultRows] = useState<SearchRow[]>(getDefaultRows());

  React.useEffect(() => {
    if (!showRows) return;
    const onDocClick = (e: MouseEvent) => {
      const el = rootRef.current;
      if (!el) return;

      // 检查点击的目标是否在下拉菜单内
      const dropdownElement = el.querySelector("[data-dropdown-content]");
      if (dropdownElement) {
        const isDropdownContent =
          dropdownElement.contains(e.target as Node) ||
          dropdownElement === e.target;
        if (isDropdownContent) {
          // 如果点击的是下拉菜单内容，不关闭下拉菜单
          return;
        }
      }

      if (!el.contains(e.target as Node)) {
        if (onCloseRows) {
          onCloseRows();
        }
      }
    };

    document.addEventListener("click", onDocClick, true);

    return () => {
      document.removeEventListener("click", onDocClick, true);
    };
  }, [showRows, onCloseRows]);

  // 搜索处理
  const handleSearch = (kw?: string | SearchRow) => {
    // 在执行搜索前关闭下拉列表
    if (onCloseRows) {
      onCloseRows();
    }
    if (onSearch) {
      onSearch(kw);
    }
  };

  // 确定显示哪些行数据
  const getDisplayRows = () => {
    // 如果有搜索结果且value不为空，显示搜索结果
    if (value && rows.length > 0) {
      return rows;
    }
    // 如果没有输入内容，显示默认数据
    if (!value && defaultRows.length > 0) {
      return defaultRows;
    }
    // 否则显示搜索结果（可能为空）
    return rows;
  };

  // 确定是否显示下拉列表
  const shouldShowRows = () => {
    // 搜索框为空时不显示下拉列表
    if (!value || !value.trim()) {
      return false;
    }
    // 优先使用外部传入的 showRows 状态
    // 这样可以避免因为 isFocused 变化导致的闪烁
    if (showRows) {
      return true;
    }
    // 输入框获得焦点时显示下拉列表
    if (isFocused) {
      return true;
    }
    // 移除自动显示空状态的逻辑，避免页面加载时闪烁
    return false;
  };

  const displayRows = getDisplayRows();
  const isShowingRows = shouldShowRows();

  return (
    <div ref={rootRef}>
      <div>
        {/* 顶部搜索区 */}
        {!compact && (
          <div className="searchPanelCard">
            {/* 1. 顶部搜索行 */}
            <div className="searchRow">
              <div className="searchBoxWrapper">
                <Input
                  placeholder={t("common.modals.search_panel_placeholder")}
                  className="searchInput"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onPressEnter={() => handleSearch(value)}
                  size="large"
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                />
                <Button
                  type="primary"
                  className="searchButton"
                  onClick={() => handleSearch(value)}
                >
                  <SearchOutlined />
                  <span>{t("common.modals.search_panel_btn")}</span>
                </Button>
                {/* 下拉框移到搜索框容器内 */}
                {isShowingRows && (
                  <div
                    data-dropdown-content
                    className="dropdownContent"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: 8,
                      zIndex: 1000,
                    }}
                  >
                    {isLoading && (
                      <div className="dropdownLoading">
                        <Spin size="small" />
                        <span>{t("common.modals.search_panel_searching")}</span>
                      </div>
                    )}
                    {!isLoading && error && (
                      <div className="dropdownError">
                        <div className="errorInner">
                          <div className="errorText">{error}</div>
                          {onRetry && (
                            <Button
                              type="default"
                              size="small"
                              onClick={() => onRetry()}
                              className="retryBtn"
                            >
                              重试
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {!isLoading && !error && displayRows.length === 0 && (
                      <div className="dropdownEmpty">
                        {t("common.modals.search_panel_no_match")}
                      </div>
                    )}
                    {!isLoading &&
                      !error &&
                      displayRows.map((row, idx) => (
                        <div
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation(); // 阻止事件冒泡
                            console.log("🎯 首页点击默认数据:", row);
                            onChange(row.brand);
                            // 优先触发 onSelectRow，它会处理数据获取和导航
                            if (onSelectRow) {
                              onSelectRow(row);
                            } else if (onSearch) {
                              // 如果没有 onSelectRow，才触发 onSearch
                              onSearch(row);
                            }
                            // 选中后关闭下拉列表（延迟执行，确保导航完成）
                            if (onCloseRows) {
                              setTimeout(() => {
                                onCloseRows();
                              }, 100);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation(); // 阻止事件冒泡
                              onChange(row.brand);
                              // 优先触发 onSelectRow，它会处理数据获取和导航
                              if (onSelectRow) {
                                onSelectRow(row);
                              } else if (onSearch) {
                                // 如果没有 onSelectRow，才触发 onSearch
                                onSearch(row);
                              }
                              // 选中后关闭下拉列表（延迟执行，确保导航完成）
                              if (onCloseRows) {
                                setTimeout(() => {
                                  onCloseRows();
                                }, 100);
                              }
                            }
                          }}
                          className="dropdownRow"
                        >
                          <div className="brandName">
                            {highlightMatch(row.brand, value)}
                          </div>
                          <div className="tags">
                            {row.currentCategoryName && (
                              <Tag className="categoryTag">
                                {highlightMatch(row.currentCategoryName, value)}
                              </Tag>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {compact && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: 0,
              margin: 0,
              width: "100%",
            }}
          >
            <div
              style={{
                width: "100%",
                // 自适应范围：桌面 410px；移动端 200–310px，随视口 56vw 变化
                maxWidth: isDesktop ? 410 : "clamp(200px, 56vw, 310px)",
                background: "transparent",
                flex: "0 1 auto",
                // 细调：整体向右偏移 10px
                marginRight: -10,
              }}
            >
              <Input
                value={value}
                onCompositionStart={(e) => {
                  inputMethodManager.startComposition();
                  setInputState((prev) => ({
                    ...prev,
                    isComposing: true,
                    isComplete: false,
                  }));
                }}
                onCompositionEnd={(e) => {
                  inputMethodManager.endComposition();
                  const target = e.target as HTMLInputElement;
                  setInputState((prev) => ({
                    ...prev,
                    isComposing: false,
                    isComplete: true,
                    lastCompleteValue: target.value,
                  }));

                  // 组合结束后立即触发搜索建议
                  if (onFetchSuggestions && target.value.trim()) {
                    onFetchSuggestions(target.value);
                  }
                }}
                onChange={(e) => {
                  onChange(e.target.value);

                  // 只在非组合状态下更新完成状态
                  if (!inputState.isComposing) {
                    setInputState((prev) => ({
                      ...prev,
                      isComplete: true,
                      lastCompleteValue: e.target.value,
                    }));

                    // 在非组合状态下，如果有关联的搜索建议函数，则调用它
                    if (onFetchSuggestions && e.target.value.trim()) {
                      onFetchSuggestions(e.target.value);
                    }
                  }
                }}
                placeholder={t("common.modals.search_panel_placeholder")}
                size="large"
                className={`compactSearchInput ${isFocused ? "compactSearchInput-focused" : ""}`}
                onFocus={() => {
                  setIsFocused(true);
                  // 每次聚焦时都重新获取数据，不使用缓存
                  if (value.trim() && onFetchSuggestions) {
                    onFetchSuggestions(value);
                  }
                }}
                onBlur={() => {
                  // 延迟失焦，给点击事件时间执行
                  setTimeout(() => {
                    setIsFocused(false);
                  }, 200);
                }}
                onPressEnter={() => {
                  // 在执行搜索前关闭下拉列表
                  if (onCloseRows) {
                    onCloseRows();
                  }
                  if (onSearch) {
                    onSearch(value);
                  }
                }}
              />
              <Button
                type="primary"
                size="middle"
                className="compactSearchButton"
                onClick={() => {
                  // 在执行搜索前关闭下拉列表
                  if (onCloseRows) {
                    onCloseRows();
                  }
                  if (onSearch) {
                    onSearch(value);
                  }
                }}
              >
                <SearchOutlined style={{ color: "white" }} />
                <span>{t("common.modals.search_panel_btn")}</span>
              </Button>

              {/* 下拉框移到搜索框容器内 */}
              {isShowingRows && (
                <div data-dropdown-content className="dropdownContent compact">
                  {isLoading && (
                    <div className="dropdownLoading">
                      <Spin size="small" />
                      <span>{t("common.modals.search_panel_searching")}</span>
                    </div>
                  )}
                  {!isLoading && error && (
                    <div className="dropdownError">
                      <div className="errorInner">
                        <div className="errorText">{error}</div>
                        {onRetry && (
                          <Button
                            type="default"
                            size="small"
                            onClick={() => onRetry()}
                            className="retryBtn"
                          >
                            重试
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  {!isLoading && !error && displayRows.length === 0 && (
                    <div className="dropdownEmpty">
                      {t("common.modals.search_panel_no_match")}
                    </div>
                  )}
                  {!isLoading &&
                    !error &&
                    displayRows.map((row, idx) => (
                      <div
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation(); // 阻止事件冒泡
                          onChange(row.brand);
                          // 优先触发 onSelectRow，它会处理数据获取和导航
                          if (onSelectRow) {
                            onSelectRow(row);
                          } else if (onSearch) {
                            // 如果没有 onSelectRow，才触发 onSearch
                            onSearch(row);
                          }
                          // 选中后关闭下拉列表（延迟执行，确保导航完成）
                          if (onCloseRows) {
                            setTimeout(() => {
                              onCloseRows();
                            }, 100);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation(); // 阻止事件冒泡
                            onChange(row.brand);
                            // 优先触发 onSelectRow，它会处理数据获取和导航
                            if (onSelectRow) {
                              onSelectRow(row);
                            } else if (onSearch) {
                              // 如果没有 onSelectRow，才触发 onSearch
                              onSearch(row);
                            }
                            // 选中后关闭下拉列表（延迟执行，确保导航完成）
                            if (onCloseRows) {
                              setTimeout(() => {
                                onCloseRows();
                              }, 100);
                            }
                          }
                        }}
                        className="dropdownRow"
                      >
                        <div className="brandName">
                          {highlightMatch(row.brand, value)}
                        </div>
                        <div className="tags">
                          {row.currentCategoryName && (
                            <Tag className="categoryTag">
                              {highlightMatch(row.currentCategoryName, value)}
                            </Tag>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPanelCard;

// 工具: 高亮匹配片段（不区分大小写）
function highlightMatch(text: string, query: string) {
  const q = (query || "").trim();
  if (!q) return text;
  const escaped = q.replace(/[.*+?^${ }()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase() ? (
          <span key={i} style={{ color: "#21C4B8", fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}
